/**
 * Atelier Diamante — Style Finder
 * Alpine.js component registered via alpine:init event.
 *
 * State machine: intro → comparing (rounds 1–5) → results
 *
 * Scoring: each comparison has a left_family / right_family.
 * Choosing a side increments that family's score by 1.
 * Skip counts as 0 for both sides.
 * Top 2 scored families → 2-3 recommendations filtered from inline catalog.
 *
 * Dispatches: CustomEvent('ad:style-finder-completed', { detail: { scores, topFamilies } })
 */

document.addEventListener('alpine:init', () => {
  Alpine.data('adStyleFinder', () => ({

    // ── State ────────────────────────────────────────────────────────────
    state:         'intro',   // 'intro' | 'comparing' | 'results'
    round:         0,         // 0-indexed, 0–4
    scores:        {},
    selections:    [],        // [{ round, chosen: 'left'|'right'|'skip', family }]
    recommendations: [],
    resultProfile: null,
    transitioning: false,

    // ── Comparison data (read from inline JSON in the section) ───────────
    pairs:   [],
    catalog: [],

    // ── Init ─────────────────────────────────────────────────────────────
    init() {
      // Read pairs from embedded JSON script tag
      const pairsEl = document.getElementById('sf-pairs-data');
      if (pairsEl) {
        try { this.pairs = JSON.parse(pairsEl.textContent); }
        catch(e) { console.error('[StyleFinder] Failed to parse pairs data', e); }
      }

      // Read catalog from embedded JSON script tag
      const catEl = document.getElementById('sf-catalog-data');
      if (catEl) {
        try { this.catalog = JSON.parse(catEl.textContent); }
        catch(e) { console.error('[StyleFinder] Failed to parse catalog data', e); }
      }

      // Initialize score map from all unique families in catalog
      const families = [...new Set(this.catalog.map(s => s.family))];
      families.forEach(f => { this.scores[f] = 0; });
    },

    // ── Computed helpers ─────────────────────────────────────────────────
    get currentPair() {
      return this.pairs[this.round] || null;
    },

    get totalRounds() {
      return this.pairs.length;
    },

    get progressDots() {
      return Array.from({ length: this.totalRounds }, (_, i) => ({
        done:    i < this.round,
        current: i === this.round,
      }));
    },

    // ── Flow ─────────────────────────────────────────────────────────────
    start() {
      this.state = 'comparing';
      this.round = 0;
    },

    choose(side) {
      if (this.transitioning || !this.currentPair) return;
      this.transitioning = true;

      const pair   = this.currentPair;
      const family = side === 'left' ? pair.left.family : pair.right.family;

      // Record selection
      this.selections.push({ round: this.round, chosen: side, family });

      // Score the chosen family
      if (family && this.scores.hasOwnProperty(family)) {
        this.scores[family]++;
      }

      // Animate, then advance
      setTimeout(() => {
        this.transitioning = false;
        this._advance();
      }, 420);
    },

    skip() {
      if (this.transitioning) return;
      this.selections.push({ round: this.round, chosen: 'skip', family: null });
      this._advance();
    },

    _advance() {
      if (this.round < this.totalRounds - 1) {
        this.round++;
      } else {
        this._computeResults();
      }
    },

    // ── Results ──────────────────────────────────────────────────────────
    _computeResults() {
      const topFamilies = this._topFamilies(2);
      this.resultProfile = this._buildProfile(topFamilies);
      this.recommendations = this._pickRecommendations(topFamilies, 3);

      this.state = 'results';

      // Analytics event
      try {
        document.dispatchEvent(new CustomEvent('ad:style-finder-completed', {
          bubbles: true,
          detail: { scores: { ...this.scores }, topFamilies }
        }));
      } catch(e) { /* non-critical */ }
    },

    _topFamilies(n) {
      return Object.entries(this.scores)
        .filter(([, score]) => score > 0)
        .sort(([, a], [, b]) => b - a)
        .slice(0, n)
        .map(([family]) => family);
    },

    _pickRecommendations(topFamilies, max) {
      if (!topFamilies.length) {
        return this.catalog.slice(0, max);
      }
      const picks = [];
      for (const family of topFamilies) {
        const inFamily = this.catalog.filter(s => s.family === family);
        for (const s of inFamily) {
          if (picks.length < max) picks.push(s);
        }
        if (picks.length >= max) break;
      }
      return picks;
    },

    // ── Editorial profiles ────────────────────────────────────────────── */
    _buildProfile(topFamilies) {
      const PROFILES = {
        'Solitaire':    {
          headline: 'Focused confidence',
          body: 'You\'re drawn to quiet confidence — settings that step back and let the stone command the room. Nothing competes, nothing distracts. Your signature is architectural, essential, built to outlast every trend.'
        },
        'Halo':         {
          headline: 'Amplified presence',
          body: 'You\'re drawn to amplified presence — settings where the stone is the star and the light plays a willing supporting role. Your aesthetic is generous, luminous, and quietly romantic without being sentimental.'
        },
        'Three-Stone':  {
          headline: 'Considered meaning',
          body: 'You\'re drawn to narrative over decoration — a ring that carries meaning as openly as it carries light. Your aesthetic is considered, intentional, and built to tell a story across generations.'
        },
        'Pavé':         {
          headline: 'Craft at close range',
          body: 'You\'re drawn to craft — a ring where every millimetre holds a decision. Your aesthetic rewards close attention and values the goldsmith\'s hand as much as the stone itself. You notice what others miss.'
        },
        'Bezel':        {
          headline: 'Clean confidence',
          body: 'You\'re drawn to clean lines and confident restraint. Your ring should work as hard as you do, sit as elegantly as you feel, and never ask anything of you in return. Security and style, undivided.'
        },
        'Cathedral':    {
          headline: 'Ceremonial elevation',
          body: 'You\'re drawn to elevation — rings that lift the stone toward the light with architectural intention. Your aesthetic is grand without being loud, ceremonial without being conventional. You understand permanence.'
        },
        'Trellis':      {
          headline: 'Structural poetry',
          body: 'You\'re drawn to structural beauty — the gallery as architecture, the prong as a line of poetry. Your aesthetic is precise, quietly astonishing at close range, and chosen by those who know what to look for.'
        },
      };

      const primary   = topFamilies[0];
      const secondary = topFamilies[1];

      if (!primary) {
        return {
          headline: 'Your own signature',
          body: 'Your taste spans the full range — you\'re not constrained by category or convention. We\'d love to help you discover exactly what that means in precious metal and stone.'
        };
      }

      const p = PROFILES[primary] || { headline: primary, body: '' };

      // Blend copy if two families scored equally
      if (secondary && this.scores[secondary] === this.scores[primary]) {
        const s = PROFILES[secondary];
        if (s) {
          return {
            headline: `${p.headline} meets ${s.headline.toLowerCase()}`,
            body: `${p.body} There\'s a secondary pull toward ${secondary.toLowerCase()} — your taste doesn\'t settle for a single answer, which means neither should your ring.`
          };
        }
      }

      return p;
    },

    // ── Restart ──────────────────────────────────────────────────────────
    restart() {
      this.state         = 'intro';
      this.round         = 0;
      this.selections    = [];
      this.recommendations = [];
      this.resultProfile = null;
      this.transitioning = false;
      // Reset all scores
      Object.keys(this.scores).forEach(k => { this.scores[k] = 0; });
    },

    // ── Image error fallback ─────────────────────────────────────────────
    onImgError(event) {
      const img = event.target;
      img.style.display = 'none';
      const placeholder = img.parentElement.querySelector('.sf-option__img-placeholder') ||
                          img.parentElement.querySelector('.sf-rec-card__img-placeholder');
      if (placeholder) placeholder.style.display = 'flex';
    },

    // ── CSS class helpers for templates ──────────────────────────────────
    optionClass(side) {
      if (!this.transitioning) return '';
      const last = this.selections[this.selections.length - 1];
      if (!last) return '';
      if (last.chosen === side)                    return 'is-chosen';
      if (last.chosen !== 'skip' && last.chosen !== side) return 'is-rejected';
      return '';
    },

  }));
});
